import { RefObject, useEffect, useRef, useState } from 'react'

import { useEffectOnce } from 'react-use'

import { DEFAULT_FIT_BOUNDS_MARGIN, DEFAULT_TRANSITION_OPTIONS } from '@web/components/Map/New/constant'
import { CustomMarkerOption, MapEventType, MarkerItem } from '@web/components/Map/New/types'

// Maps API v3 사내 Docs https://pages.oss.navercorp.com/Maps/maps.js/docs/naver.maps.html
export const useMap = (
  containerRef: RefObject<HTMLDivElement>,
  mapOptions?: naver.maps.MapOptions,
  onMapReady?: (map: naver.maps.Map) => void,
) => {
  const [map, setMap] = useState<naver.maps.Map>()

  useEffectOnce(() => {
    if (!containerRef.current) throw Error('There is no Map Container')

    const _map = new naver.maps.Map(containerRef.current, mapOptions)
    onMapReady?.(_map)
    setMap(_map)
  })

  return { map }
}

export const useMarkers = (map: naver.maps.Map | undefined, markerOptions: CustomMarkerOption[]) => {
  const [markers, setMarkers] = useState<MarkerItem[]>([])

  useEffect(() => {
    if (!map || markerOptions.length === 0) return () => {}

    const capturedMarkers: MarkerItem[] = markerOptions.map(option => ({
      option,
      marker: new naver.maps.Marker({ ...option, map }),
    }))

    setMarkers(capturedMarkers)

    return () => capturedMarkers.map(marker => marker.marker.setMap(null))
  }, [map, markerOptions])

  return markers
}

export const useFitBounds = (map: naver.maps.Map | undefined, markers: MarkerItem[], enableFitBounds: boolean) => {
  useEffect(() => {
    if (map && enableFitBounds) {
      if (markers.length === 0) return

      if (markers.length === 1) {
        map.setZoom(12)
        map.setCenter(markers[0].marker.getPosition())
      } else {
        const path = markers.map(({ marker }) => marker.getPosition())
        const polyline = new naver.maps.Polyline({ path })
        const markersBounds = polyline.getBounds()
        map.panToBounds(markersBounds, DEFAULT_TRANSITION_OPTIONS, DEFAULT_FIT_BOUNDS_MARGIN)
      }
    }
  }, [map, markers, enableFitBounds])
}

export const useMapListener = (
  map: naver.maps.Map | undefined,
  eventName: MapEventType,
  callback: (event: any) => void,
) => {
  const cbRef = useRef(callback)
  cbRef.current = callback

  useEffect(() => {
    if (!map) return () => {}

    const listener = naver.maps.Event.addListener(map, eventName, event => cbRef.current(event))
    return () => naver.maps.Event.removeListener(listener)
  }, [map, eventName])
}
